import { useState, useCallback, useMemo } from 'react';

import { useCartMutations } from './useCartMutations';
import { dispatchCartRefreshEvent } from '../../lib';

import type { CartItemInput, CheckoutContextValue, CreateEmptyCartData } from '../../types';

/**
 * Composition hook for checkout state management
 * Simple approach: cart ID in state only, no localStorage persistence
 *
 * useMemo stabilizes the returned object reference used as context value.
 * React Compiler handles this automatically, but we keep useMemo
 * as an explicit safety net for clarity.
 */
export function useCheckoutState(): CheckoutContextValue {
  const [cartId, setCartId] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    createGuestCart,
    addToCart,
    isCreatingCart,
  } = useCartMutations({
    onAddToCartSuccess: dispatchCartRefreshEvent,
    onError: setErrorMessage,
    setAddingToCart,
  });

  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  /**
   * Add product to cart
   * Creates cart first if needed, then adds item
   */
  const handleAddToCart = useCallback(
    (data: CartItemInput) => {
      setErrorMessage(null);
      setAddingToCart(data.sku);

      if (!cartId) {
        // No cart yet - create one first, then add product
        createGuestCart(undefined, {
          onSuccess: (response: CreateEmptyCartData) => {
            const newCartId = response?.createEmptyCart ?? null;
            setCartId(newCartId);
            if (newCartId) {
              addToCart({ cartId: newCartId, data });
            } else {
              setAddingToCart(null);
              setErrorMessage('Failed to create cart. Please try again.');
            }
          },
        });
      } else {
        // Cart exists - add product directly
        addToCart({ cartId, data });
      }
    },
    [cartId, createGuestCart, addToCart],
  );

  return useMemo(
    () => ({
      handleAddToCart,
      clearError,
      addingToCart,
      errorMessage,
      isInitializing: isCreatingCart,
    }),
    [handleAddToCart, clearError, addingToCart, errorMessage, isCreatingCart]
  );
}
